/**
 * Slide configuration
 *
 * All the shared styles and timing for slides live here.
 *
 * summary:
 * - Base and per-type styles consumed by `SlideWrapper`
 * - Constants for slide types and default durations
 * - Helpers: `getSlideStyles` and `getSlideDuration`
 */

// Base styles that apply to all slides
export const BASE_SLIDE_STYLES = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
};

// Slide-specific styles based on type
export const SLIDE_TYPE_STYLES = {
  intro: {
    bgcolor: "background.paper", // white background
    minHeight: "100vh",
  },
  announcement: {
    bgcolor: "background.paper", // white background
    minHeight: "100vh",
    flexDirection: "column",
  },
  map: {
    "& .kiosk-pigeon-map-root": {
      width: "100%",
      height: "100%",
      position: "relative",
    },
    "& .pigeon-zoom-buttons": {
      zIndex: 1000,
    },
  },
  windBarbMap: {
    "& .kiosk-pigeon-map-root": {
      width: "100%",
      height: "100%",
      position: "relative",
    },
    "& .pigeon-zoom-buttons": {
      zIndex: 1000,
    },
  },
  station: {
    bgcolor: "background.default",
    minHeight: "100vh",
    p: { xs: 1, sm: 3 },

    // Center everything:
    // flexDirection: "column",
    // alignItems: "center",
    // justifyContent: "center",

    // Horizontal layout:
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
  },
};

// Slide type definitions
export const SLIDE_TYPES = {
  INTRO: "intro",
  ANNOUNCEMENT: "announcement",
  MAP: "map",
  WIND_BARB_MAP: "windBarbMap",
  STATION: "station",
};

// Slide duration configuration (in milliseconds)
export const SLIDE_DURATIONS = {
  [SLIDE_TYPES.INTRO]: 5000,
  [SLIDE_TYPES.ANNOUNCEMENT]: 5000,
  [SLIDE_TYPES.MAP]: 5000,
  [SLIDE_TYPES.WIND_BARB_MAP]: 5000,
  [SLIDE_TYPES.STATION]: 8000, // Default for stations, can be overridden by timer config
};

// Auto-resume configuration
export const AUTO_RESUME_ENABLED = true;
export const AUTO_RESUME_TIMEOUT = 15000; // milliseconds

// Hide controls/cursor configuration
export const HIDE_CURSOR_TIMEOUT = 5000; // milliseconds

/**
 * Get styles for a specific slide type
 * @param {string} slideType - The type of slide
 * @param {Object} customStyles - Additional custom styles to merge
 * @returns {Object} Combined styles for the slide
 */
export function getSlideStyles(slideType, customStyles = {}) {
  const baseStyles = BASE_SLIDE_STYLES;
  const typeStyles = SLIDE_TYPE_STYLES[slideType] || {};

  return {
    ...baseStyles,
    ...typeStyles,
    ...customStyles,
  };
}

/**
 * Get duration for a specific slide type
 * @param {string} slideType - The type of slide
 * @param {number} customDuration - Custom duration (for stations with timer config)
 * @returns {number} Duration in milliseconds
 */
export function getSlideDuration(slideType, customDuration = null) {
  if (customDuration !== null) {
    return customDuration * 1000; // Convert seconds to milliseconds
  }
  return SLIDE_DURATIONS[slideType] || 5000;
}
